<template>
    <v-container fluid class="event-log-page">
        <div class="event-log-heading">
            <h3 class="event-log-title text--white80">EVENT LOG</h3>
        </div>
        <div class="event-log-scroll">
            <v-timeline v-if="logItems && userSettings"
                        class="event-log-timeline"
                        dense
                        :class="{ 'transparent-line': logItems.length <= 1 }"
                        clipped>
                <v-timeline-item v-for="(item, index) in logItems"
                                 :key="index"
                                 color="white">
                    <template v-slot:icon>
                        <v-avatar size="40">
                            <img src="@/assets/icons/reliable-b-m-s.svg" v-if="item.latestEventName.indexOf('No event') !== -1" />
                            <img src="@/assets/icons/unlock.svg" v-else-if="item.latestEventName.indexOf('Armed') !== -1" />
                            <img src="@/assets/icons/lock.svg" v-else-if="item.latestEventName.indexOf('Disarmed') !== -1" />
                            <img src="@/assets/icons/siren-red.svg" v-else-if="item.latestEventName.startsWith('Alarm')" />
                            <img src="@/assets/icons/tamper-triggered.svg" v-else-if="item.latestEventName.indexOf('Alarm - Tamper triggered') !== -1" />
                            <img src="@/assets/icons/immobilizer-off.svg" v-else-if="item.latestEventName.indexOf('Manual immobilizer off') !== -1" />
                            <img src="@/assets/icons/power-loss.svg" v-else-if="item.latestEventName.indexOf('Main power loss') !== -1" />
                            <img src="@/assets/icons/power-restore.svg" v-else-if="item.latestEventName.indexOf('Main power restored') !== -1" />
                            <img src="@/assets/icons/main-batt-low.svg" v-else-if="item.latestEventName.indexOf('Main battery low') !== -1" />
                            <img src="@/assets/icons/main-batt-ok.svg" v-else-if="item.latestEventName.indexOf('Main battery OK') !== -1" />
                            <img src="@/assets/icons/backup-batt-low.svg" v-else-if="item.latestEventName.indexOf('Backup battery low') !== -1" />
                            <img src="@/assets/icons/backup-batt-ok.svg" v-else-if="item.latestEventName.indexOf('Backup battery OK') !== -1" />
                            <img src="@/assets/icons/restarted.svg" v-else-if="item.latestEventName.indexOf('Restarted') !== -1" />
                            <img src="@/assets/icons/factory-reset.svg" v-else-if="item.latestEventName.indexOf('Factory reset') !== -1" />
                            <img src="@/assets/icons/geo-fence-breached.svg" v-else-if="item.latestEventName.indexOf('Geofence breach') !== -1" />
                            <img src="@/assets/icons/online.svg" v-else-if="item.latestEventName.indexOf('Online') !== -1" />
                            <img src="@/assets/icons/offline.svg" v-else-if="item.latestEventName.indexOf('Offline') !== -1" />
                            <img src="@/assets/icons/unknown-error.svg" v-else-if="item.latestEventName.indexOf('Unknown event') !== -1" />
                            <img src="@/assets/icons/unknown-error.svg" v-else />
                        </v-avatar>
                    </template>
                    <v-row class="event-row no-gutters" align="center">
                        <v-col v-if="item.latestEventName.indexOf('shore') !== -1" class="event-name white--text">
                            Shore Power disabled
                        </v-col>
                        <v-col v-else-if="item.latestEventName.indexOf('smart') !== -1" class="event-name white--text">
                            Smart Plug disabled
                        </v-col>
                        <v-col v-else class="event-name white--text">
                            {{ item.latestEventName }}
                        </v-col>
                        <v-col class="event-date text-right white--text">
                            <time :datetime="item.modifiedDate">
                                <span class="event-date__day">{{ formatModifiedDate(item.modifiedDate) }}</span>
                                <span class="event-date__time">{{ formatModifiedTime(item.modifiedDate) }}</span>
                            </time>
                        </v-col>
                    </v-row>
                </v-timeline-item>
            </v-timeline>
        </div>
    </v-container>
</template>

<script lang="ts">
    import { defineComponent, ref, computed } from "vue";
    import useCoreHistory from "@/store/useCoreHistory";
    import moment from 'moment-timezone';
    import useUserSettings from "@/store/useUserSettings";
    import UserSettingsViewModel from "@/models/UserSettingsViewModel";

    export default defineComponent({
        setup(_, context) {
            const { getUserSettings } = useUserSettings();
            const userSettings = ref<UserSettingsViewModel>();
            getUserSettings().then((value: UserSettingsViewModel) => {
                userSettings.value = value;
            });

            const { logItems } = useCoreHistory();

            const timezone = computed(() => userSettings.value?.timezone || "UTC");

            const formatModifiedDate = (modifiedDate: string) => {
                return moment.utc(modifiedDate).tz(timezone.value).format("MMM DD, YYYY");
            };

            const formatModifiedTime = (modifiedDate: string) => {
                return moment.utc(modifiedDate).tz(timezone.value).format("HH:mm:ss");
            };

            return {
                logItems,
                userSettings,
                timezone,
                formatModifiedDate,
                formatModifiedTime
            };
        }
    });
</script>
<style lang="scss" scoped>
    .event-log-page {
        position: absolute;
        inset: 0;
        display: flex;
        width: 100%;
        max-width: 100%;
        min-height: 0;
        flex-direction: column;
        overflow: hidden;
        box-sizing: border-box;
        margin: 0 auto;
        padding: 0 clamp(12px, 2.5vw, 20px);
    }

    .event-log-heading {
        flex: 0 0 auto;
        padding: 10px 0 8px;
        text-align: center;
    }

    .event-log-title {
        margin-bottom: 0;
        font-size: var(--app-font-emphasis);
        line-height: 1.2;
    }

    .event-log-scroll {
        flex: 1 1 auto;
        min-height: 0;
        padding: 0 12px 12px 0;
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
    }

    .event-log-timeline {
        --event-divider-width: 64px;
        --event-leading-gap: 6px;
        margin: 0;
        padding-right: 0;
        overflow: visible;
    }

    .event-log-timeline ::v-deep .v-timeline-item__divider {
        min-width: var(--event-divider-width);
    }

    .event-log-timeline ::v-deep .v-timeline-item__body {
        max-width: calc(100% - var(--event-divider-width) - var(--event-leading-gap));
    }

    .event-name,
    .event-date {
        padding: 3px 4px !important;
        font-size: var(--app-font-body);
        line-height: 1.2;
    }

    .event-row {
        flex-wrap: nowrap;
        gap: 8px;
    }

    .event-name {
        min-width: 0;
        flex: 1 1 auto;
    }

    .event-date {
        display: flex;
        width: max-content;
        min-width: max-content;
        flex: 0 0 auto;
        flex-direction: column;
        align-items: flex-end;
        font-size: var(--app-font-caption);
        line-height: 1.1;
    }

    .event-date time {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 1px;
    }

    .event-date__day {
        color: rgba(255, 255, 255, 0.7);
        font-size: var(--app-font-tiny);
        font-weight: 400;
    }

    .event-date__time {
        color: rgba(255, 255, 255, 0.95);
        font-size: var(--app-font-small);
        font-weight: 600;
    }

    ::v-deep .v-timeline-item {
        padding-bottom: 12px;
    }

    .theme--light.v-timeline:before {
        background: white;
        right: auto !important;
        left: 37px !important;
        top: 40px;
        height: auto;
    }

    .theme--light.v-timeline-item {
        align-items: center;
    }

    .v-timeline-item:last-child {
        padding-bottom: 0;
    }

    .transparent-line::v-deep.v-timeline:before {
        background: transparent !important;
    }
</style>